<template>
  <footer class="footer-root">
    <div class="footer-main">
      <div class="m-container">
        <div class="m-row py-8">
          <!-- Spalte 1: Logo, Beschreibung, Trust -->
          <div class="m-col m-col-12 m-col-md-4">
            <NuxtLink to="/">
              <img src="~/assets/img/moebix-logo-white.svg" alt="Moebix" height="64" />
            </NuxtLink>
            <p class="footer-desc text-body-2 mt-4 mb-4">
              Dein Möbel-Portal für Preisvergleich, Magazin und Einrichtungsideen. Kostenlos, unabhängig und immer aktuell.
            </p>

            <div class="d-flex flex-column m-gap-3">
              <div class="d-flex align-center m-gap-2 trust-item">
                <MIcon icon="shield-check" :size="18" class="trust-icon" />
                <span class="text-body-2">Kostenlos &amp; unabhängig</span>
              </div>
              <div class="d-flex align-center m-gap-2 trust-item">
                <MIcon icon="store" :size="18" class="trust-icon" />
                <span class="text-body-2">Über 500 Shops verglichen</span>
              </div>
              <div class="d-flex align-center m-gap-2 trust-item">
                <MIcon icon="lock" :size="18" class="trust-icon" />
                <span class="text-body-2">Datenschutz-konform</span>
              </div>
            </div>
          </div>

          <!-- Spalte 2: Entdecken -->
          <div class="m-col m-col-6 m-col-sm-3 m-col-md-2">
            <h4 class="footer-heading mb-4">Entdecken</h4>
            <div class="d-flex flex-column m-gap-2">
              <NuxtLink v-for="link in navLinks" :key="link.id" :to="link.path" class="footer-link">
                {{ link.title }}
              </NuxtLink>
            </div>
          </div>

          <!-- Spalte 3: Kategorien -->
          <div class="m-col m-col-6 m-col-sm-3 m-col-md-4">
            <h4 class="footer-heading mb-4">Kategorien</h4>
            <div class="footer-cat-grid">
              <NuxtLink v-for="cat in navigationStore.items" :key="cat.id" :to="cat.path" class="footer-link">
                {{ cat.name }}
              </NuxtLink>
            </div>
          </div>

          <!-- Spalte 4: Rechtliches -->
          <div class="m-col m-col-6 m-col-sm-3 m-col-md-2">
            <h4 class="footer-heading mb-4">Rechtliches</h4>
            <div class="d-flex flex-column m-gap-2">
              <NuxtLink v-for="link in legalLinks" :key="link.id" :to="link.path || '#'" class="footer-link">
                {{ link.title }}
              </NuxtLink>
            </div>
          </div>

          <!-- Spalte 5: Social -->
          <div class="m-col m-col-6 m-col-sm-3 m-col-md-2">
            <h4 class="footer-heading mb-4">Folge uns</h4>
            <div class="d-flex flex-wrap m-gap-2">
              <MBtn v-for="social in ['facebook', 'instagram', 'pinterest', 'youtube']" :key="social" icon variant="outlined" color="white" size="small" class="social-btn" :aria-label="`Moebix auf ${social.charAt(0).toUpperCase() + social.slice(1)}`">
                <MIcon :icon="social" :size="18" />
              </MBtn>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Unterer Footer -->
    <div class="footer-bottom">
      <div class="m-container">
        <div class="footer-bottom__inner">
          <span>&copy; {{ currentYear }} Moebix.de</span>
          <span class="footer-bottom__sep">·</span>
          <span>Alle Rechte vorbehalten</span>
          <span class="footer-bottom__sep">·</span>
          <span>Preise inkl. MwSt., zzgl. Versand</span>
          <span class="footer-bottom__sep">·</span>
          <span>Gemacht mit <MIcon icon="heart" :size="11" color="error" /> in Germania</span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
const api = useApi();
const navigationStore = useNavigationStore();

onMounted(() => {
  navigationStore.fetchNavigation();
});

const currentYear = computed(() => new Date().getFullYear());

const { data: navData } = useAsyncData("footer-nav",
  () => api<any>("/api/site-content", { query: { section: "footer_nav" } }),
  { lazy: true },
);
const { data: legalData } = useAsyncData("footer-legal",
  () => api<any>("/api/site-content", { query: { section: "footer_legal" } }),
  { lazy: true },
);

const navLinks = computed(() => navData.value?.items || []);
const legalLinks = computed(() =>
  (legalData.value?.items || []).filter((l: any) => !l.title?.toLowerCase().includes("agb")),
);
</script>

<style scoped>
.footer-root { display: block; width: 100%; }
.footer-main { background: linear-gradient(160deg, #3c8f93 0%, #2d6e72 60%, #1e4e51 100%); color: #fff; }
.footer-bottom { background: #1a3d3f; }
.footer-heading { font-size: 0.8125rem; font-weight: 700; color: rgba(255, 255, 255, 0.8); text-transform: uppercase; letter-spacing: 0.08em; }
.footer-desc { line-height: 1.65; max-width: 340px; opacity: 0.8; color: #fff; }
.footer-link { color: rgba(255, 255, 255, 0.85); text-decoration: none; font-size: 0.875rem; transition: color var(--m-transition-fast); }
.footer-link:hover { color: #fff; }
.social-btn { opacity: 0.7; transition: opacity 0.15s ease; }
.social-btn:hover { opacity: 1; }
.trust-item { opacity: 0.85; }
.trust-icon { color: rgba(255, 255, 255, 0.6); flex-shrink: 0; }
.footer-bottom__inner {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 12px 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

.footer-bottom__sep {
  color: rgba(255, 255, 255, 0.25);
}

@media (max-width: 599px) {
  .footer-bottom__inner {
    flex-direction: column;
    gap: 2px;
  }
  .footer-bottom__sep { display: none; }
}
.footer-cat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
</style>
